/* =============================================================================
   NAJIBS — product page behaviour
   <najibs-variant-picker> · <najibs-gallery> (incl. zoom) · <najibs-sticky-atc>

   Everything here builds on assets/global.js: focus trapping, scroll locking,
   money formatting, the pub/sub bus and the ajax cart already live there and are
   never re-implemented. The file is safe to evaluate twice and every element
   re-initialises itself cleanly under shopify:section:load.
   ============================================================================= */
(function () {
  'use strict';

  const NAJIBS = (window.NAJIBS = window.NAJIBS || {});
  if (NAJIBS.__productLoaded) return;
  NAJIBS.__productLoaded = true;

  const DESKTOP = '(min-width: 750px)';

  const reduced = () => (NAJIBS.prefersReducedMotion ? NAJIBS.prefersReducedMotion() : false);
  const isDesktop = () => window.matchMedia(DESKTOP).matches;
  const rootOf = (el) => el.closest('[data-product-root]') || document;

  function readJSON(el) {
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (err) {
      return null;
    }
  }

  function money(cents) {
    return NAJIBS.money ? NAJIBS.money(cents) : String(cents);
  }

  /* The inline payload from snippets/product-variant-picker.liquid is trimmed to the
     fields this file reads, so option values are taken positionally from option1/2/3
     rather than from a full options array. */
  function optionAt(variant, index) {
    if (!variant) return null;
    return variant['option' + (index + 1)];
  }

  /* ------------------------------------------------------------------ price */

  function setMoney(el, cents) {
    const label = el.querySelector('.visually-hidden');
    el.textContent = '';
    if (label) el.appendChild(label);
    el.appendChild(document.createTextNode(money(cents)));
  }

  function updatePrice(scope, variant) {
    if (!variant) return;
    scope.querySelectorAll('[data-price-target]').forEach((target) => {
      const price = target.querySelector('.price');
      const regular = target.querySelector('.price__regular');
      if (!regular) return;

      setMoney(regular, variant.price);

      const onSale = Boolean(variant.compare_at_price && variant.compare_at_price > variant.price);
      let compare = target.querySelector('.price__compare');

      if (onSale) {
        if (!compare) {
          compare = document.createElement('s');
          compare.className = 'price__compare';
          const label = regular.querySelector('.visually-hidden');
          if (label) compare.appendChild(label.cloneNode(true));
          regular.insertAdjacentElement('afterend', compare);
        }
        setMoney(compare, variant.compare_at_price);
      } else if (compare) {
        compare.remove();
      }

      if (price) price.classList.toggle('price--on-sale', onSale);
    });
  }

  /* -------------------------------------------------------------------- sku */

  function updateSku(scope, variant) {
    const wrap = scope.querySelector('[data-sku]');
    if (!wrap) return;
    const sku = variant && variant.sku ? variant.sku : '';
    const value = wrap.querySelector('[data-sku-value]');
    if (value) value.textContent = sku;
    wrap.hidden = !sku;
  }

  /* -------------------------------------------------------------- inventory */

  function updateInventory(scope, variant) {
    const el = scope.querySelector('[data-inventory]');
    if (!el) return;

    if (!el.__najibsInventory) {
      el.__najibsInventory = readJSON(scope.querySelector('[data-variant-inventory]')) || {};
    }

    const info = variant ? el.__najibsInventory[String(variant.id)] : null;
    const text = el.querySelector('[data-inventory-text]');
    const threshold = Number(el.dataset.threshold || 0);

    let state = 'none';
    let message = '';

    /* Nothing is guessed: without Shopify tracking the quantity there is no line. */
    if (info && info.managed) {
      const qty = Number(info.qty || 0);
      if (qty > 0 && qty <= threshold) {
        state = 'low';
        message = (el.dataset.labelLow || '').replace('[count]', String(qty));
      } else if (qty > 0) {
        state = 'in';
        message = el.dataset.labelIn || '';
      } else if (info.policy === 'continue') {
        state = 'backorder';
        message = el.dataset.labelBackorder || '';
      }
    }

    el.dataset.state = state;
    if (text) text.textContent = message;
    el.hidden = state === 'none';
  }

  /* ----------------------------------------------------------- availability */

  function updateAvailability(scope, variant) {
    const strings = NAJIBS.strings || {};
    let label = strings.addToCart || '';
    let disabled = false;

    if (!variant) {
      label = strings.unavailable || label;
      disabled = true;
    } else if (!variant.available) {
      label = strings.soldOut || label;
      disabled = true;
    }

    scope.querySelectorAll('[data-add-button], [data-sticky-add]').forEach((button) => {
      button.disabled = disabled;
    });
    scope.querySelectorAll('[data-add-button-text]').forEach((el) => {
      el.textContent = label;
    });
  }

  /* =================================================== <najibs-variant-picker> */

  class NajibsVariantPicker extends HTMLElement {
    connectedCallback() {
      if (this.__init) return;
      this.__init = true;

      this.root = rootOf(this);
      this.variants = readJSON(this.querySelector('[data-variant-json]')) || [];
      this.optionCount = Number(this.dataset.optionCount || 0);
      this.inputs = Array.from(this.querySelectorAll('input[type="radio"][data-option-position]'));

      this.onChange = (event) => {
        if (event.target.matches('input[type="radio"][data-option-position]')) this.select();
      };
      this.addEventListener('change', this.onChange);

      this.refreshAvailability();
    }

    get selection() {
      const values = new Array(this.optionCount).fill(null);
      this.inputs.forEach((input) => {
        if (!input.checked) return;
        values[Number(input.dataset.optionPosition) - 1] = input.value;
      });
      return values;
    }

    findVariant(values) {
      if (!this.optionCount) return this.variants[0] || null;
      return (
        this.variants.find((variant) =>
          values.every((value, index) => value !== null && optionAt(variant, index) === value)
        ) || null
      );
    }

    /* A value is offered when Shopify can actually sell it alongside the rest of
       the current selection. Nothing is hidden — only marked. */
    isValueAvailable(position, value) {
      const values = this.selection;
      return this.variants.some((variant) => {
        if (!variant.available) return false;
        for (let i = 0; i < this.optionCount; i += 1) {
          const expected = i === position - 1 ? value : values[i];
          if (expected === null) continue;
          if (optionAt(variant, i) !== expected) return false;
        }
        return true;
      });
    }

    refreshAvailability() {
      /* Liquid has already worked out what cannot be sold and marked it in the markup.
         With no usable payload, `some()` over an empty list answers "unavailable" for
         every value — which would strike through the entire size range while the button
         still reads "Add to bag". On a phone that is the whole picker gone. Deferring to
         the server-rendered state is the honest answer when there is nothing to add. */
      if (!this.variants.length) return;

      const note = this.dataset.unavailableLabel || '';
      this.inputs.forEach((input) => {
        const ok = this.isValueAvailable(Number(input.dataset.optionPosition), input.value);
        if (ok) input.removeAttribute('aria-disabled');
        else input.setAttribute('aria-disabled', 'true');

        const label = input.closest('label');
        if (!label) return;
        label.classList.toggle('is-unavailable', !ok);
        const noteEl = label.querySelector('[data-value-note]');
        if (noteEl) noteEl.textContent = ok ? '' : note;
      });
    }

    updateLabels() {
      this.inputs.forEach((input) => {
        if (!input.checked) return;
        const target = this.querySelector(
          '[data-option-selected-value="' + input.dataset.optionPosition + '"]'
        );
        if (target) target.textContent = input.value;
      });
    }

    updateForm(variant) {
      this.root.querySelectorAll('input[data-variant-id]').forEach((input) => {
        input.value = variant ? variant.id : '';
        input.disabled = !variant;
      });
    }

    updateUrl(variant) {
      if (!variant || !window.history || !window.history.replaceState) return;
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      } catch (err) {
        /* opaque or unusual URL — the selection still works, the address bar just stays put */
      }
    }

    select() {
      const variant = this.findVariant(this.selection);

      this.refreshAvailability();
      this.updateLabels();
      this.updateForm(variant);
      this.updateUrl(variant);

      updateAvailability(this.root, variant);
      updatePrice(this.root, variant);
      updateSku(this.root, variant);
      updateInventory(this.root, variant);

      NAJIBS.publish(NAJIBS.EVENTS.variantChange, {
        variant: variant,
        root: this.root,
        sectionId: this.dataset.sectionId
      });
    }
  }

  /* ========================================================== <najibs-gallery> */

  class NajibsGallery extends HTMLElement {
    connectedCallback() {
      if (this.__init) return;
      this.__init = true;

      this.layout = this.dataset.layout || 'thumbnails';
      this.track = this.querySelector('[data-gallery-track]');
      this.items = Array.from(this.querySelectorAll('[data-gallery-item]'));
      this.thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
      this.dots = Array.from(this.querySelectorAll('[data-gallery-dot]'));
      this.counter = this.querySelector('[data-gallery-counter]');
      this.lightbox = this.querySelector('[data-gallery-lightbox]');
      this.zoomOpen = false;

      const active = this.items.findIndex((item) => item.classList.contains('is-active'));
      this.index = active < 0 ? 0 : active;

      this.thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => this.select(Number(thumb.dataset.index), true));
      });
      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => this.select(Number(dot.dataset.index), true));
      });

      this.onClick = (event) => {
        const trigger = event.target.closest('[data-gallery-zoom]');
        if (!trigger || !this.contains(trigger)) return;
        event.preventDefault();
        this.openZoom(Number(trigger.dataset.index));
      };
      this.addEventListener('click', this.onClick);

      /* The active slide drives the dots and the counter. `scrollend` fires once the
         phone's momentum has actually settled on a snap point, so it is exact; the
         debounced scroll handler stays as the fallback for engines without it and keeps
         the counter moving mid-swipe rather than only at the end. */
      if (this.track) {
        this.onScroll = NAJIBS.debounce(() => this.syncFromScroll(), 70);
        this.track.addEventListener('scroll', this.onScroll, { passive: true });

        if ('onscrollend' in this.track) {
          this.onScrollEnd = () => this.syncFromScroll();
          this.track.addEventListener('scrollend', this.onScrollEnd, { passive: true });
        }
      }

      if (this.lightbox) {
        this.onKey = (event) => {
          if (event.key !== 'Escape') return;
          event.stopPropagation();
          this.closeZoom();
        };
        this.lightbox.addEventListener('click', (event) => {
          if (event.target.closest('[data-lightbox-close]') || event.target === this.lightbox) {
            this.closeZoom();
          }
        });
      }

      this.unsubscribe = NAJIBS.subscribe(NAJIBS.EVENTS.variantChange, (data) => {
        if (!data || !data.variant) return;
        if (data.root && data.root !== rootOf(this)) return;
        const media = data.variant.featured_media;
        if (!media) return;
        const index = this.items.findIndex((item) => String(item.dataset.mediaId) === String(media.id));
        if (index >= 0) this.select(index, true);
      });

      this.select(this.index, false);
    }

    disconnectedCallback() {
      if (this.unsubscribe) this.unsubscribe();
      if (this.track && this.onScroll) this.track.removeEventListener('scroll', this.onScroll);
      if (this.track && this.onScrollEnd) this.track.removeEventListener('scrollend', this.onScrollEnd);
      if (this.zoomOpen) this.closeZoom();
    }

    select(index, scroll) {
      if (!this.items.length) return;
      const next = Math.min(Math.max(index, 0), this.items.length - 1);
      this.index = next;

      this.items.forEach((item, i) => item.classList.toggle('is-active', i === next));

      this.thumbs.forEach((thumb, i) => {
        const on = i === next;
        thumb.classList.toggle('is-active', on);
        thumb.setAttribute('aria-selected', on ? 'true' : 'false');
      });

      this.dots.forEach((dot, i) => {
        const on = i === next;
        dot.classList.toggle('is-active', on);
        if (on) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });

      if (this.counter) {
        const template = this.dataset.counterLabel || '';
        this.counter.textContent = template
          .replace('[index]', String(next + 1))
          .replace('[total]', String(this.items.length));
      }

      this.pauseInactiveVideos(next);
      if (scroll) this.scrollToItem(next);
    }

    scrollToItem(index) {
      const item = this.items[index];
      if (!item) return;
      const behavior = reduced() ? 'auto' : 'smooth';

      if (isDesktop()) {
        /* The thumbnails layout swaps the frame in place — nothing to scroll. */
        if (this.layout === 'thumbnails') return;
        item.scrollIntoView({ behavior: behavior, block: 'center' });
        return;
      }

      if (this.track) {
        this.track.scrollTo({ left: item.offsetLeft - this.track.offsetLeft, behavior: behavior });
      }
    }

    syncFromScroll() {
      if (!this.track || isDesktop()) return;
      const middle = Math.abs(this.track.scrollLeft) + this.track.clientWidth / 2;
      let closest = 0;
      let best = Infinity;
      this.items.forEach((item, i) => {
        const center = Math.abs(item.offsetLeft - this.track.offsetLeft) + item.clientWidth / 2;
        const distance = Math.abs(center - middle);
        if (distance < best) {
          best = distance;
          closest = i;
        }
      });
      if (closest !== this.index) this.select(closest, false);
    }

    pauseInactiveVideos(index) {
      this.items.forEach((item, i) => {
        if (i === index) return;
        item.querySelectorAll('video').forEach((video) => {
          if (!video.paused) video.pause();
        });
      });
    }

    openZoom(index) {
      if (!this.lightbox || this.zoomOpen) return;
      this.zoomOpen = true;
      this.zoomReturn = document.activeElement;
      this.lightbox.hidden = false;
      if (NAJIBS.lockScroll) NAJIBS.lockScroll();
      document.addEventListener('keydown', this.onKey);

      requestAnimationFrame(() => {
        this.lightbox.classList.add('is-active');
        const target = this.lightbox.querySelector('[data-zoom-item="' + index + '"]');
        if (target) target.scrollIntoView({ block: 'start', behavior: 'auto' });
        const close = this.lightbox.querySelector('[data-lightbox-close]');
        if (NAJIBS.trapFocus) NAJIBS.trapFocus(this.lightbox, close);
      });
    }

    closeZoom() {
      if (!this.lightbox || !this.zoomOpen) return;
      this.zoomOpen = false;
      this.lightbox.classList.remove('is-active');
      document.removeEventListener('keydown', this.onKey);
      if (NAJIBS.removeTrapFocus) NAJIBS.removeTrapFocus(this.zoomReturn);
      if (NAJIBS.unlockScroll) NAJIBS.unlockScroll();

      const done = () => {
        if (!this.zoomOpen) this.lightbox.hidden = true;
      };
      if (reduced()) done();
      else window.setTimeout(done, 260);
    }
  }

  /* ======================================================= <najibs-sticky-atc> */

  class NajibsStickyAtc extends HTMLElement {
    connectedCallback() {
      if (this.__init) return;
      this.__init = true;

      this.root = rootOf(this);
      this.form =
        (this.dataset.form && document.getElementById(this.dataset.form)) ||
        (this.root.querySelector ? this.root.querySelector('form[data-ajax-cart]') : null);
      if (!this.form || !('IntersectionObserver' in window)) return;

      this.visible = false;
      this.formVisible = true;
      this.nearBottom = false;

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            this.formVisible = entry.isIntersecting;
          });
          this.update();
        },
        { rootMargin: '0px 0px -15% 0px' }
      );
      this.observer.observe(this.form);

      this.watchFooter();
    }

    /* The bar must never sit permanently over the footer: on a phone that is where the
       shop's own links, policies and contact details live, and a fixed bar hides the last
       ~64px of the page for the whole visit.

       The footer itself is the thing being protected, so the footer itself is what is
       observed — a scroll-position guess is wrong the moment the footer's height changes
       (a long link list, a newsletter block, a stacked mobile layout). The old distance
       heuristic is kept only for the case where no footer element is on the page. */
    watchFooter() {
      const footer =
        document.querySelector('[data-site-footer]') ||
        document.querySelector('.footer') ||
        document.querySelector('footer');

      if (footer) {
        this.footerObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              this.nearBottom = entry.isIntersecting;
            });
            this.update();
          },
          /* A sliver of footer counts: the bar should already be gone by the time the
             visitor can read the first line of it. */
          { threshold: 0 }
        );
        this.footerObserver.observe(footer);
        return;
      }

      this.onScroll = () => {
        if (this.frame) return;
        this.frame = window.requestAnimationFrame(() => {
          this.frame = null;
          const bottom = window.scrollY + window.innerHeight;
          this.nearBottom = bottom >= document.documentElement.scrollHeight - 200;
          this.update();
        });
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.footerObserver) this.footerObserver.disconnect();
      if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
      if (this.frame) window.cancelAnimationFrame(this.frame);
    }

    update() {
      const show = !this.formVisible && !this.nearBottom;
      if (show === this.visible) return;
      this.visible = show;

      if (show) {
        this.hidden = false;
        window.requestAnimationFrame(() => {
          if (this.visible) this.classList.add('is-visible');
        });
        return;
      }

      this.classList.remove('is-visible');
      const done = () => {
        if (!this.visible) this.hidden = true;
      };
      if (reduced()) done();
      else window.setTimeout(done, 280);
    }
  }

  /* ------------------------------------------------------------------ share */

  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-share-button]');
    if (!button) return;
    event.preventDefault();

    /* The live address is the real product URL, including the chosen variant. */
    const url = window.location.href || button.dataset.shareUrl;
    const title = button.dataset.shareTitle || document.title;

    if (navigator.share) {
      try {
        await navigator.share({ title: title, url: url });
        return;
      } catch (err) {
        if (err && err.name === 'AbortError') return;
      }
    }

    const feedback = button.parentElement
      ? button.parentElement.querySelector('[data-share-feedback]')
      : null;

    try {
      await navigator.clipboard.writeText(url);
      if (feedback) {
        feedback.textContent = button.dataset.copiedLabel || '';
        feedback.hidden = false;
        window.setTimeout(() => {
          feedback.hidden = true;
        }, 2600);
      }
      if (NAJIBS.announce) NAJIBS.announce(button.dataset.copiedLabel || '');
    } catch (err) {
      /* No clipboard permission — hand the address over so it can be copied by hand. */
      window.prompt(title, url);
    }
  });

  /* ------------------------------------------------------------------- boot */

  function enableVariantInputs(scope) {
    /* The hidden id input ships disabled so the <noscript> select stays authoritative
       when scripting is off. With scripting on, it becomes the source of truth. */
    (scope || document).querySelectorAll('input[data-variant-id][disabled]').forEach((input) => {
      if (input.value) input.disabled = false;
    });
  }

  let remembered = false;

  function rememberProduct() {
    if (remembered || !NAJIBS.rememberProduct) return;
    const root = document.querySelector('[data-product-root][data-remember="true"]');
    if (!root || !root.dataset.productHandle) return;
    remembered = true;
    NAJIBS.rememberProduct(root.dataset.productHandle);
  }

  function boot(scope) {
    enableVariantInputs(scope);
    rememberProduct();
  }

  /* global.js copies only the *inner* HTML of [data-quick-add-content] into the modal, so
     the section's own [data-product-root] wrapper does not survive the move. The modal
     target stands in for it — without a root of its own, a size change in the modal would
     rewrite every price on the page behind it. Any picker that upgraded during the
     injection had already cached document as its scope, so it is re-pointed here. */
  function bootQuickAdd(container) {
    if (!container) return;

    if (!container.closest('[data-product-root]')) {
      container.setAttribute('data-product-root', '');
    }

    container.querySelectorAll('najibs-variant-picker').forEach((picker) => {
      if (picker.__init) picker.root = rootOf(picker);
    });

    boot(container);
  }

  if (NAJIBS.subscribe) {
    NAJIBS.subscribe('quickadd:loaded', (data) => bootQuickAdd(data && data.container));
  }

  if (!customElements.get('najibs-variant-picker')) {
    customElements.define('najibs-variant-picker', NajibsVariantPicker);
  }
  if (!customElements.get('najibs-gallery')) {
    customElements.define('najibs-gallery', NajibsGallery);
  }
  if (!customElements.get('najibs-sticky-atc')) {
    customElements.define('najibs-sticky-atc', NajibsStickyAtc);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot(document));
  } else {
    boot(document);
  }

  document.addEventListener('shopify:section:load', (event) => boot(event.target));
})();
